import fetchStream from "@/utils/fetchStream";
import fetchData from "@/utils/fetchData";
import pushNextPage from "@/utils/pushNextPage";
import { newTour } from "@/utils/shepherd/main";
import { streamEvents } from "@/utils/streamEvents";
import { JsonListParser } from "@/utils/jsonList";
import { guessOutput } from "@/utils/markdown";
import { fillBlanks, listBlanks, sameBlank } from "@/utils/blanks";
import { remixInputs } from "@/utils/remix";
import { lengthRequested } from "./length";
import { splitSentences } from "@/utils/sentences";

const MAX_VERSIONS = 20;
// The marks of the last change to part of a picture, so trying again keeps them
let lastMarkedEdit = null;

// The first make someone ever gets opens wide to read; after that the panel stays the
// width they leave it
const WIDE_SEEN_KEY = "wideResultSeen";
function wideResultSeen() {
  try {
    return localStorage.getItem(WIDE_SEEN_KEY) === "true";
  } catch {
    return true;
  }
}
function rememberWideResult() {
  try {
    localStorage.setItem(WIDE_SEEN_KEY, "true");
  } catch {
    // Private windows can refuse storage; it just opens wide again next time
  }
}
const IMAGES_KEPT = 3;
import {
  startStream,
  stopStream,
  finishStream,
  isCurrentStream,
} from "@/utils/streamControllers";
import {
  StreamError,
  isRetryable,
  RETRY_LIMIT,
  retryDelay,
} from "@/utils/streamList";

export default {
  name: "result",
  namespaced: true,
  state: {
    loading: false,
    loadingFix: false,
    commentToChange: "",
    completeFixedText: "",
    generatedText: "",
    finishedText: [],
    editMode: false,
    showResult: false,
    // On computers the make can widen over the tabs to be read more easily
    expanded: false,
    // The blank being filled in, while the fill-in bar is open
    fillingBlank: "",
    vote: 0,
    error: "",
    // What the make was delivered as: text, docx, pdf, xlsx or image
    output: "",
    image: "",
    imageLoading: false,
    // The change being made to the picture by an edit, if any
    imageChange: "",
    // When a picture is refused: { message, suggestion } with an idea we can make
    refusal: null,
    imageError: "",
    // "researching" while the web is searched, then "" once writing starts
    stage: "",
    // Pages the research used: [{ url, title }]
    sources: [],
    // The id of this make in the user's saved makes
    makeId: "",
    // Earlier versions to go back to: [{ text, output, image, sources, createdAt }]
    versions: [],
    versionIndex: -1,
  },
  mutations: {
    SET_LOADING(state, payload) {
      state.loading = payload;
    },
    SET_LOADING_FIX(state, payload) {
      state.loadingFix = payload;
    },
    SET_COMMENT_TO_CHANGE(state, payload) {
      state.commentToChange = payload;
    },
    SET_COMPLETE_FIXED_TEXT(state, payload) {
      state.completeFixedText = payload;
    },
    SET_GENERATED_TEXT(state, payload) {
      state.generatedText = payload;
    },
    SET_FINISHED_TEXT(state, payload) {
      state.finishedText = payload;
    },
    APPEND_GENERATED_TEXT(state, payload) {
      state.generatedText += payload;
    },
    SET_EDIT_MODE(state, payload) {
      state.editMode = payload;
    },
    SET_SHOW_RESULT(state, payload) {
      state.showResult = payload;
    },
    SET_EXPANDED(state, payload) {
      state.expanded = payload;
    },
    SET_FILLING_BLANK(state, payload) {
      state.fillingBlank = payload;
    },
    SET_VOTE(state, payload) {
      state.vote = payload;
    },
    SET_ERROR(state, payload) {
      state.error = payload;
    },
    SET_OUTPUT(state, payload) {
      state.output = payload;
    },
    SET_IMAGE(state, payload) {
      state.image = payload;
    },
    SET_IMAGE_CHANGE(state, payload) {
      state.imageChange = payload;
    },
    SET_REFUSAL(state, payload) {
      state.refusal = payload;
    },
    SET_IMAGE_LOADING(state, payload) {
      state.imageLoading = payload;
    },
    SET_IMAGE_ERROR(state, payload) {
      state.imageError = payload;
    },
    SET_STAGE(state, payload) {
      state.stage = payload;
    },
    SET_SOURCES(state, payload) {
      state.sources = payload;
    },
    SET_MAKE_ID(state, payload) {
      state.makeId = payload;
    },
    SET_VERSIONS(state, payload) {
      state.versions = payload;
    },
    SET_VERSION_INDEX(state, payload) {
      state.versionIndex = payload;
    },
  },
  actions: {
    toggleExpanded({ commit, state }) {
      commit("SET_EXPANDED", !state.expanded);
    },
    // Opens the fill-in bar at a blank, or the first one
    startFillingBlanks({ commit, state }, blank) {
      const blanks = listBlanks(state.generatedText);
      commit(
        "SET_FILLING_BLANK",
        blanks.find((each) => sameBlank(each, blank)) || blanks[0] || ""
      );
    },
    reset({ commit, dispatch }) {
      stopStream("result");
      stopStream("fixText");
      commit("SET_FILLING_BLANK", "");
      commit("SET_GENERATED_TEXT", "");
      commit("SET_VOTE", 0);
      commit("SET_SHOW_RESULT", false);
      commit("SET_FINISHED_TEXT", []);
      commit("SET_LOADING", false);
      commit("SET_LOADING_FIX", false);
      commit("SET_COMMENT_TO_CHANGE", "");
      commit("SET_COMPLETE_FIXED_TEXT", "");
      commit("SET_EDIT_MODE", false);
      commit("SET_ERROR", "");
      commit("SET_REFUSAL", null);
      commit("SET_MAKE_ID", "");
      commit("SET_VERSIONS", []);
      commit("SET_VERSION_INDEX", -1);
      dispatch("clearOutput");
    },
    // Adds the current result as a new version. A first drawing of an image
    // belongs to the version it was drawn for.
    recordVersion({ commit, state }) {
      const snapshot = {
        text: state.generatedText,
        output: state.output || "text",
        image: state.image,
        sources: state.sources,
        createdAt: new Date().toISOString(),
      };
      const latest = state.versions[state.versions.length - 1];
      let versions;
      if (
        latest &&
        snapshot.image &&
        !latest.image &&
        latest.text === snapshot.text
      ) {
        versions = [...state.versions.slice(0, -1), { ...latest, ...snapshot }];
      } else {
        versions = [...state.versions, snapshot].slice(-MAX_VERSIONS);
      }
      // Pictures are large; only the newest few versions keep theirs
      let images = 0;
      versions = versions
        .slice()
        .reverse()
        .map((version) => {
          if (!version.image) return version;
          images += 1;
          return images > IMAGES_KEPT ? { ...version, image: "" } : version;
        })
        .reverse();
      commit("SET_VERSIONS", versions);
      commit("SET_VERSION_INDEX", versions.length - 1);
    },
    selectVersion({ commit, dispatch, state }, index) {
      const version = state.versions[index];
      if (!version || state.loading || state.loadingFix) return;
      stopStream("image");
      commit("SET_IMAGE_LOADING", false);
      commit("SET_IMAGE_ERROR", "");
      commit("SET_VERSION_INDEX", index);
      commit("SET_OUTPUT", version.output);
      commit("SET_GENERATED_TEXT", version.text);
      commit("SET_IMAGE", version.image || "");
      commit("SET_SOURCES", version.sources || []);
      dispatch("convertToFinished", version.text);
      dispatch("saveMake");
    },
    // Keeps the finished make in the user's account
    async saveMake({ commit, state, rootState }) {
      if (!rootState.auth.loggedIn || !state.generatedText) return;
      const makeId = state.makeId;
      const { response } = await fetchData(
        "makes/save",
        {
          id: makeId || undefined,
          prompt: rootState.prompt,
          output: state.output || "text",
          text: state.generatedText,
          image: state.image || undefined,
          sources: state.sources,
          versions: state.versions,
          current: state.versionIndex,
          // What went into it, so it can be remixed if shared
          inputs: remixInputs(rootState),
        },
        false,
        false,
        false
      );
      // Only adopt the id if this is still the same make
      if (
        response.status === 201 &&
        response.result?.id &&
        state.makeId === makeId
      ) {
        commit("SET_MAKE_ID", response.result.id);
      }
    },
    // A public link to this make, saving it first if it hasn't been yet
    async shareLink({ dispatch, state }) {
      if (!state.makeId) {
        await dispatch("saveMake");
      }
      if (!state.makeId) return "";
      const { response } = await fetchData(
        "makes/share",
        { id: state.makeId },
        false,
        false,
        false
      );
      return response.status === 201 ? response.result?.url || "" : "";
    },
    // Shows a make opened from My makes
    showSavedMake({ commit, dispatch }, make) {
      commit("SET_MAKE_ID", make.id);
      commit("SET_OUTPUT", make.output || "text");
      commit("SET_GENERATED_TEXT", make.text || "");
      commit("SET_IMAGE", make.image || "");
      commit("SET_SOURCES", Array.isArray(make.sources) ? make.sources : []);
      const versions =
        Array.isArray(make.versions) && make.versions.length
          ? make.versions
          : [
              {
                text: make.text || "",
                output: make.output || "text",
                image: make.image || "",
                sources: make.sources || [],
                createdAt: make.updatedAt,
              },
            ];
      commit("SET_VERSIONS", versions);
      commit(
        "SET_VERSION_INDEX",
        Number.isInteger(make.current) && versions[make.current]
          ? make.current
          : versions.length - 1
      );
      commit("SET_ERROR", "");
      commit("SET_LOADING", false);
      dispatch("convertToFinished", make.text || "");
      commit("SET_SHOW_RESULT", true);
    },
    clearOutput({ commit }) {
      stopStream("image");
      commit("SET_OUTPUT", "");
      commit("SET_IMAGE", "");
      commit("SET_IMAGE_LOADING", false);
      commit("SET_IMAGE_ERROR", "");
      commit("SET_STAGE", "");
      commit("SET_SOURCES", []);
    },
    resetLoading({ commit, state, dispatch }) {
      if (state.loading) {
        commit("SET_LOADING", false);
        dispatch("getResult");
      } else if (state.imageLoading) {
        dispatch("getImage");
      }
    },
    hideResult({ commit, rootState }) {
      commit("SET_EDIT_MODE", false);
      commit("SET_SHOW_RESULT", false);
      pushNextPage(rootState);
    },
    // Back to the make from its questions and style
    showMake({ commit, rootState }) {
      commit("SET_SHOW_RESULT", true);
      pushNextPage(rootState);
    },
    convertToFinished({ commit }, textToConvert) {
      textToConvert = textToConvert.replace(" [[", "[[");
      textToConvert = textToConvert.replace("]] ", "]]");
      const newLines = textToConvert.split(/(\r\n|\n|\r)/g);
      let linesWithBrackets = [];
      newLines.forEach((line) => {
        let temp = line.split(/(\[\[|\]\])/g);
        linesWithBrackets.push(...temp);
      });

      let lines = [];
      // split text into sentences, breaking on periods, question marks, exclamation marks and new lines
      linesWithBrackets.forEach((line) => {
        let temp2 = splitSentences(line);
        lines.push(...temp2);
      });
      lines = lines.map((line) => line || " ");
      // remove items in array that are " " or ""
      lines = lines.filter((line) => line !== " " && line !== "");
      let sentences = [];
      // add a space to the end of each sentence, unless it's the last sentence or it is a \n
      let changed = false;
      lines.forEach((line, index) => {
        if (line.includes("[[")) {
          changed = true;
          line = line.replace("[[", "");
          return;
        }
        if (line.includes("]]")) {
          changed = false;
          line = line.replace("]]", "");
          return;
        }
        if (index === lines.length - 1) {
          sentences.push({
            text: line,
            changed,
            highlightable: !(line === " " || line === "\n"),
          });
          return;
        }
        // if the nxt line is a \n, push the line without a space
        if (lines[index + 1] === "\n") {
          sentences.push({
            text: line,
            changed,
            highlightable: !(line === " " || line === "\n"),
          });
          return;
        }
        // push a item that is " " after the sentence item
        sentences.push({
          text: line,
          changed,
          highlightable: !(line === " " || line === "\n"),
        });
        sentences.push({ text: " ", changed, highlightable: false });
      });
      commit("SET_FINISHED_TEXT", sentences);
    },
    async getResult(
      { dispatch, commit, state, getters, rootState },
      { regenerate = false, retries = 0 } = {}
    ) {
      if (!rootState.auth.loggedIn) {
        dispatch("modals/openAuth", null, { root: true });
        return;
      }
      const controller = startStream("result");
      stopStream("fixText");
      commit("SET_FILLING_BLANK", "");
      commit("SET_GENERATED_TEXT", "");
      commit("SET_FINISHED_TEXT", []);
      commit("SET_VOTE", 0);
      commit("SET_EDIT_MODE", false);
      commit("SET_ERROR", "");
      commit("SET_REFUSAL", null);
      commit("SET_LOADING_FIX", false);
      dispatch("clearOutput");
      commit("SET_LOADING", true);
      commit("SET_SHOW_RESULT", true);
      if (retries === 0 && !rootState.isMobile && !wideResultSeen()) {
        commit("SET_EXPANDED", true);
        rememberWideResult();
      }
      if (retries === 0) {
        pushNextPage(rootState);
        if (!regenerate) {
          dispatch("personalInfo/incrementTotalMakes", null, { root: true });
        }
      }
      try {
        const { reader, status, message, body } = await fetchStream(
          "generate/final",
          { info: getters.getInfo, regenerate },
          true,
          true,
          false,
          controller.signal
        );
        if (status === 401) {
          dispatch("auth/sessionExpired", null, { root: true });
          commit("SET_ERROR", "Please log in to generate your make");
          dispatch("modals/openAuth", null, { root: true });
        } else if (status === 422 && body?.unsafe) {
          // A picture we won't make: say so kindly and offer one we will
          commit("SET_REFUSAL", {
            message,
            suggestion: body.suggestion || "",
          });
        } else if (status === 402) {
          commit("SET_ERROR", "You've used all your wishes");
          dispatch("wishes/openModal", "empty", { root: true });
          dispatch("wishes/fetchBalance", null, { root: true });
        } else if (status === 429) {
          commit(
            "SET_ERROR",
            "You're making things very quickly, please wait a moment and try again."
          );
        } else if (!reader) {
          throw new StreamError("Internal Server Error");
        } else {
          for await (const event of streamEvents(reader)) {
            if (event.error) {
              throw new StreamError(event.error);
            }
            if (typeof event.wishes === "number") {
              dispatch("wishes/setBalance", event.wishes, { root: true });
            }
            if (typeof event.output === "string") {
              commit("SET_OUTPUT", event.output);
            }
            if (event.stage === "researching") {
              commit("SET_STAGE", "researching");
            }
            if (Array.isArray(event.sources)) {
              commit(
                "SET_SOURCES",
                event.sources.filter(
                  (source) =>
                    typeof source?.url === "string" &&
                    /^https?:\/\//.test(source.url)
                )
              );
            }
            if (typeof event.text === "string") {
              commit("SET_STAGE", "");
              commit("APPEND_GENERATED_TEXT", event.text);
            }
          }
          if (state.generatedText === "") {
            throw new StreamError("Generated text is empty");
          }
          if (!state.output) {
            commit("SET_OUTPUT", guessOutput(state.generatedText));
          }
          dispatch("convertToFinished", state.generatedText);
          dispatch("recordVersion");
          await dispatch("saveMake");
          if (state.output === "image") {
            dispatch("getImage");
          }
        }
      } catch (error) {
        if (!isCurrentStream("result", controller)) return;
        if (isRetryable(error) && retries < RETRY_LIMIT) {
          setTimeout(() => {
            if (isCurrentStream("result", controller)) {
              dispatch("getResult", { regenerate, retries: retries + 1 });
            }
          }, retryDelay(retries));
          return;
        }
        console.error(error);
        commit("SET_ERROR", "An error has occurred. Please try again.");
        dispatch("wishes/fetchBalance", null, { root: true });
      }
      if (isCurrentStream("result", controller)) {
        finishStream("result", controller);
        commit("SET_LOADING", false);
        newTour();
      }
    },
    // An image make's text describes the picture; this draws it. After an edit,
    // `change` is what was asked for, and the current picture is changed instead.
    // With `mask` (see PictureMarker) only the marked parts are redrawn.
    async getImage(
      { commit, dispatch, state, rootState },
      { change, mask } = {}
    ) {
      const controller = startStream("image");
      const edit =
        typeof change === "string" && change.trim() && state.image
          ? change.trim()
          : "";
      // Trying a marked change again keeps its marks
      if (edit && typeof mask === "string") lastMarkedEdit = { edit, mask };
      const marks =
        edit && lastMarkedEdit?.edit === edit ? lastMarkedEdit.mask : "";
      commit("SET_IMAGE_CHANGE", edit);
      commit("SET_IMAGE_ERROR", "");
      commit("SET_REFUSAL", null);
      commit("SET_IMAGE_LOADING", true);
      const { response } = await fetchData("generate/image", {
        description: state.generatedText,
        shape: rootState.output.imageShape,
        ...(edit ? { picture: state.image, change: edit } : {}),
        ...(marks ? { mask: marks } : {}),
      });
      if (!isCurrentStream("image", controller)) return;
      finishStream("image", controller);
      const image = response.result?.image;
      dispatch("wishes/setBalance", response.result?.wishes, { root: true });
      if (response.status === 422 && response.result?.unsafe) {
        commit("SET_REFUSAL", {
          message: response.result.message,
          suggestion: response.result.suggestion || "",
        });
        commit("SET_IMAGE_CHANGE", "");
      } else if (response.status === 402) {
        commit("SET_IMAGE_ERROR", "You need more wishes to draw this");
        dispatch("wishes/openModal", "empty", { root: true });
      } else if (response.status === 201 && typeof image === "string") {
        commit("SET_IMAGE", image);
        commit("SET_IMAGE_CHANGE", "");
        dispatch("recordVersion");
        dispatch("saveMake");
      } else if (response.status === 401) {
        commit("SET_IMAGE_ERROR", "Please log in to draw your make");
      } else {
        commit(
          "SET_IMAGE_ERROR",
          state.imageChange
            ? "We couldn't change the picture, please try again"
            : "We couldn't draw your make, please try again"
        );
      }
      commit("SET_IMAGE_LOADING", false);
    },
    // Redraws only the marked parts of the picture. Like any change to a picture, the
    // description is brought up to date first, so later versions still match it.
    redrawPart({ dispatch, state }, { mask, change }) {
      if (!state.image || !mask || !change?.trim()) return;
      return dispatch("getChange", {
        originalText: state.generatedText,
        textToChange: [],
        commentToChange: change.trim(),
        selectedAll: true,
        mask,
      });
    },
    // Starts a new make from the picture idea offered after a refusal, keeping the
    // chosen output and shape
    async useSafeSuggestion({ state, commit, dispatch, rootState }) {
      const suggestion = state.refusal?.suggestion;
      if (!suggestion) return;
      const { activeOutput, imageShape } = rootState.output;
      commit("SET_REFUSAL", null);
      await dispatch(
        "changePrompt",
        { prompt: suggestion, force: true },
        { root: true }
      );
      dispatch("output/selectOutput", activeOutput, { root: true });
      dispatch("output/selectImageShape", imageShape, { root: true });
    },
    // Puts the user's answers into the blanks the AI left, as a new version
    fillBlanks({ state, commit, dispatch }, values) {
      const filled = fillBlanks(state.generatedText, values);
      if (filled === state.generatedText) return;
      commit("SET_GENERATED_TEXT", filled);
      dispatch("convertToFinished", filled);
      dispatch("recordVersion");
      dispatch("saveMake");
    },
    applyChange({ state, commit, dispatch }, change) {
      if (
        !change ||
        typeof change.original !== "string" ||
        typeof change.replacement !== "string"
      ) {
        return;
      }
      // Changes can reach past the words picked (a heading's section, say), and the
      // model may copy the picked text with its [[marks]] or stray spaces
      const unmark = (text) => text.replace(/\[\[|\]\]/g, "").trim();
      const original = unmark(change.original);
      if (!original || !state.completeFixedText.includes(original)) return;
      commit(
        "SET_COMPLETE_FIXED_TEXT",
        state.completeFixedText.replace(
          original,
          "[[" + unmark(change.replacement) + "]]"
        )
      );
      dispatch("convertToFinished", state.completeFixedText);
    },
    async getChange(
      { dispatch, commit, state, getters, rootState },
      { originalText, textToChange, commentToChange, selectedAll, mask }
    ) {
      if (state.loading) return;
      const controller = startStream("fixText");
      commit("SET_LOADING_FIX", true);
      commit("SET_ERROR", "");
      commit("SET_COMPLETE_FIXED_TEXT", state.generatedText);
      pushNextPage(rootState);
      try {
        const { reader, status } = await fetchStream(
          "generate/fixText",
          {
            info: { ...getters.getInfo, output: state.output || undefined },
            originalText,
            textToChange,
            commentToChange,
            selectedAll,
          },
          true,
          true,
          false,
          controller.signal
        );
        if (status === 401) {
          dispatch("auth/sessionExpired", null, { root: true });
          commit("SET_ERROR", "Please log in to generate your make");
          dispatch("modals/openAuth", null, { root: true });
        } else if (!reader) {
          throw new StreamError("Internal Server Error");
        } else {
          const parser = new JsonListParser();
          for await (const event of streamEvents(reader)) {
            if (event.error) {
              throw new StreamError(event.error);
            }
            if (typeof event.text === "string") {
              for (const change of parser.push(event.text)) {
                dispatch("applyChange", change);
              }
            }
          }
          commit(
            "SET_GENERATED_TEXT",
            state.completeFixedText.replace(/\[\[/g, "").replace(/\]\]/g, "")
          );
          if (state.output === "image") {
            // The new version is recorded once its picture is ready
            dispatch("getImage", { change: commentToChange, mask });
          } else {
            dispatch("recordVersion");
            dispatch("saveMake");
          }
        }
      } catch (error) {
        if (!isCurrentStream("fixText", controller)) return;
        console.error(error);
        commit("SET_ERROR", "We couldn't make that change. Please try again.");
      }
      if (isCurrentStream("fixText", controller)) {
        finishStream("fixText", controller);
        commit("SET_LOADING_FIX", false);
      }
    },
    deleteSentence({ commit, state }, indexArray) {
      let newFinishedText = state.finishedText;
      indexArray.forEach((index) => {
        if (!newFinishedText[index]) return;
        // if there is a space directly after the sentence, replace it with an empty string
        if (newFinishedText[index + 1]?.text === " ") {
          newFinishedText[index + 1].text = "";
        }
        // replace the sentence with an empty string
        newFinishedText[index].text = "";
        newFinishedText[index].highlightable = false;
      });
      let newlineCount = 0;
      newFinishedText.forEach((item) => {
        // if there are more than 2 new lines in a row, separated by empty strings, or spaces, remove until only 2 new lines are left
        if (item.text === "\n" && newlineCount < 2) {
          newlineCount++;
        } else if (item.text === "\n") {
          item.text = "";
        } else if (item.text !== " " && item.text !== "") {
          newlineCount = 0;
        }
      });
      commit("SET_FINISHED_TEXT", newFinishedText);
      // flatten state.finishedText.text and set the generatedText to the new text
      let newGeneratedText = state.finishedText
        .map((item) => item.text)
        .join("");
      commit("SET_GENERATED_TEXT", newGeneratedText);
    },
    toggleEditMode({ commit, state }) {
      commit("SET_FILLING_BLANK", "");
      commit("SET_EDIT_MODE", !state.editMode);
      // map state.finishedText to to set all changed to false
      state.finishedText.map((item) => (item.changed = false));
    },
    setVote({ commit, state, rootState }, vote) {
      if (state.vote === vote) {
        commit("SET_VOTE", 0);
      } else {
        commit("SET_VOTE", vote);
      }
      if (!rootState.auth.loggedIn) return;
      fetchData(
        "log/vote",
        {
          vote: state.vote,
        },
        false,
        true,
        false
      );
    },
  },
  getters: {
    getAnsweredQuestions(state, getters, rootState) {
      if (rootState.questions.chosenAnswers.length === 0) return [];
      let answered = [];
      rootState.questions.chosenAnswers.forEach((item) => {
        if (item.options.length === 0) return;
        answered.push({
          question: item.question.question,
          answers: item.options,
        });
      });
      return answered;
    },
    getAnsweredLength(state, getters, rootState) {
      return lengthRequested(rootState.length);
    },
    getInfo(state, getters, rootState, rootGetters) {
      const info = {
        questions: getters.getAnsweredQuestions,
      };
      if (rootState.context.context) {
        info.context = rootState.context.context;
      }
      if (rootState.tone.activeTone) {
        info.tone = rootState.tone.activeTone;
      }
      if (rootState.format.activeFormat) {
        info.format = rootState.format.activeFormat;
      }
      info.length = getters.getAnsweredLength;
      if (rootState.language.activeLanguage) {
        info.language = rootState.language.activeLanguage;
      }
      if (rootState.output.activeOutput !== "auto") {
        info.output = rootState.output.activeOutput;
      }
      if (rootState.context.research) {
        info.research = true;
      }
      if (rootState.context.useProfile === false) {
        info.useProfile = false;
      }
      const documents = rootGetters["context/documentsForRequest"];
      if (documents.length) {
        info.documents = documents;
      }
      const images = rootGetters["context/imagesForRequest"];
      if (images.length) {
        info.images = images;
      }
      return info;
    },
  },
  modules: {},
};
